/**
 * OrganizationInvitationPage
 * ==========================
 *
 * Landing page for organization-invitation emails
 * (``/organization-invitation/<token>``). Verifies the token, shows
 * who's inviting you to what, and lets you accept or decline.
 *
 * Works logged-out too: the token is stashed in localStorage
 * (``pendingOrgInviteToken``) and the user is sent to the login page —
 * the post-login bootstrap (verify-magic-link.html / Google / SSO)
 * brings them straight back here to finish accepting. This is how
 * "invite by email -> they create their account -> they're joined"
 * completes for brand-new users.
 *
 * Backend (via the ``organizations`` redux-saga namespace):
 *   GET  /organization/verify-invitation?token=      (public)
 *   POST /organization/invitations/<id>/accept       (authed)
 *   POST /organization/invitations/<id>/decline
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography,
} from '@mui/material';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { isAuthenticated, getCurrentUser } from '../../utils/localUser';
import {
  fetchRequest as verifyRequest,
  clear as clearVerify,
} from '../../redux/actions/organizations/invitationVerify';
import {
  fetchRequest as respondRequest,
  clear as clearRespond,
} from '../../redux/actions/organizations/invitationRespond';

const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const PENDING_KEY = 'pendingOrgInviteToken';

const OrganizationInvitationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const verifySlice = useSelector((s) => s.organization_invitation_verify) || {};
  const respondSlice = useSelector((s) => s.organization_invitation_respond) || {};
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    dispatch(clearRespond());
    dispatch(clearVerify());
    dispatch(verifyRequest({ token }));
    setInitialized(true);
  }, [token, dispatch]);

  const loading = !initialized || verifySlice.isLoading;
  const invitation = verifySlice.response;
  const error = verifySlice.error || respondSlice.error;
  const busy = Boolean(respondSlice.isLoading);
  // 'accepted' | 'declined' | ''
  const done = respondSlice.response
    ? (respondSlice.response.verb === 'accept' ? 'accepted' : 'declined')
    : '';

  // Once the invitation has been answered, the stashed token has served
  // its purpose.
  useEffect(() => {
    if (respondSlice.response) localStorage.removeItem(PENDING_KEY);
  }, [respondSlice.response]);

  const authed = isAuthenticated();
  const me = authed ? getCurrentUser() : null;

  const handleSignIn = () => {
    localStorage.setItem(PENDING_KEY, token);
    window.location.href = '/login.html';
  };

  const respond = (verb) => {
    dispatch(respondRequest({ invitationId: invitation.invitation_id, verb }));
  };

  const emailMismatch = Boolean(
    authed && me?.email && invitation?.email
    && me.email.toLowerCase() !== String(invitation.email).toLowerCase(),
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 460, width: '100%', borderRadius: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <CorporateFareIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Organization invitation
          </Typography>
        </Stack>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        )}

        {!loading && error && !done && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button variant="outlined" onClick={() => navigate('/')}>Go to the app</Button>
          </>
        )}

        {!loading && !error && invitation && !done && (
          <>
            <Typography variant="body1" sx={{ mb: 1 }}>
              You've been invited to join{' '}
              <strong>{invitation.organization_name || 'an organization'}</strong>
              {' '}as{' '}
              <Chip size="small" label={cap(invitation.role || 'member')} sx={{ mx: 0.25 }} />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Invitation sent to <strong>{invitation.email}</strong>.
            </Typography>

            {!authed ? (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Sign in (or create your account) with{' '}
                  <strong>{invitation.email}</strong> to accept — you'll be
                  brought right back here.
                </Alert>
                <Button variant="contained" fullWidth onClick={handleSignIn}>
                  Sign in to accept
                </Button>
              </>
            ) : (
              <>
                {emailMismatch && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You're signed in as <strong>{me.email}</strong>, but this
                    invitation was sent to <strong>{invitation.email}</strong>.
                    Accepting will add your current account to the organization.
                  </Alert>
                )}
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained"
                    onClick={() => respond('accept')}
                    disabled={busy}
                  >
                    {busy ? 'Working…' : 'Accept invitation'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => respond('decline')}
                    disabled={busy}
                  >
                    Decline
                  </Button>
                </Stack>
              </>
            )}
          </>
        )}

        {done === 'accepted' && (
          <>
            <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success" sx={{ mb: 2 }}>
              You've joined{' '}
              <strong>{invitation?.organization_name || 'the organization'}</strong>.
            </Alert>
            <Button variant="contained" onClick={() => { window.location.href = '/'; }}>
              Open the app
            </Button>
          </>
        )}

        {done === 'declined' && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>Invitation declined.</Alert>
            <Button variant="outlined" onClick={() => navigate('/')}>Go to the app</Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default OrganizationInvitationPage;
